import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highcharts";
import HighchartsReact from "highcharts-react-official";
import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}
initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

function truncateChars(str, max = 15) {
  const s = String(str ?? "");
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

export default function TopVehiclesQualitativeStackedBarChart({
  categories = [],
  positive = [],
  neutral = [],
  negative = [],
  loading = false,
  title = "Top 10 - Publicações por veículo (Qualitativo)",
  filename = "top-10-veiculos-qualitativo",
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const CARD_H = 360;
  const CHART_H = 350;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        width: "100%",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        height: CARD_H,
        minHeight: CARD_H,
      };
    }

    return {
      position: "relative",
      width: "100%",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      height: 560,
      minHeight: 560,
    };
  }, [exportMode]);

  const options = useMemo(() => {
    const EXPORT_H = 560;

    return {
      ...baseChartOptions,

      chart: {
        type: "bar",
        height: exportMode ? EXPORT_H : CHART_H,
        spacing: exportMode ? [0, 0, 0, 0] : [24, 16, 16, 30],
        backgroundColor: "transparent",
        animation: !exportMode,
        marginTop: exportMode ? 0 : 64,

        marginLeft: exportMode ? 200 : 170,

        scrollablePlotArea: exportMode
          ? undefined
          : {
              minWidth: 520,
              scrollPositionX: 1,
            },
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: "#614a9e" },
      },

      exporting: { enabled: false },

      xAxis: {
        categories,
        tickLength: 0,
        lineWidth: 0,
        labels: {
          style: {
            fontSize: exportMode ? "12px" : "11px",
            fontWeight: "800",
            color: "var(--text-color)",
          },
          useHTML: true,
          formatter: function () {
            const full = String(this.value || "");
            const short = truncateChars(full, exportMode ? 24 : 15);

            const fullEsc = escapeHtml(full);
            const shortEsc = escapeHtml(short);

            return `<span title="${fullEsc}">${shortEsc}</span>`;
          },
        },
      },

      yAxis: {
        min: 0,
        title: { text: null },
        gridLineDashStyle: "ShortDot",
        gridLineColor: "rgba(17, 24, 39, 0.10)",
        labels: {
          style: {
            fontSize: exportMode ? "12px" : "11px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
      },

      legend: exportMode
        ? { enabled: false }
        : {
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            itemStyle: {
              fontWeight: "700",
              fontSize: "12px",
              color: "var(--text-color)",
            },
          },

      tooltip: {
        shared: true,
        useHTML: true,
      },

      plotOptions: {
        series: {
          stacking: "normal",
          borderWidth: 0,
          groupPadding: exportMode ? 0.12 : 0.18,
          pointPadding: exportMode ? 0.06 : 0.08,
          animation: !exportMode,

          dataLabels: {
            enabled: true,
            inside: true,
            style: {
              fontWeight: "900",
              fontSize: exportMode ? "17px" : "11px",
              color: "#ffffff",
              textOutline: "none",
            },
            formatter: function () {
              return this.y ? this.y : "";
            },
          },
        },
      },

      series: [
        { name: "Positiva", data: positive, color: "#16A34A" },
        { name: "Neutra", data: neutral, color: "#F59E0B" },
        { name: "Negativa", data: negative, color: "#EF4444" },
      ],

      credits: { enabled: false },
    };
  }, [categories, positive, neutral, negative, title, filename, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();
        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, categories, positive, neutral, negative]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart?.exportChart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease, box-shadow .2s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando top veículos (qualitativo)...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{
            style: { height: exportMode ? 560 : CHART_H, width: "100%" },
          }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
